import { useSyncExternalStore } from 'react';

/**
 * One clock, shared by every countdown in the app.
 *
 * Each consumer used to own its interval, which meant two views of the same
 * deadline sampled `Date.now()` at different moments: a list row could read
 * "20:43" while the header pill beside it read "20:44". Reading one snapshot
 * from one timer makes that impossible rather than unlikely.
 *
 * A second is the finest granularity anything displays, so there is nothing to
 * gain from a slower tier for the "20d" case — a handful of leaf components
 * re-rendering once a second costs nothing next to the coordination a
 * per-consumer interval would need.
 */
const TICK_MS = 1_000;

let now = Date.now();
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | undefined;

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);

  if (timer === undefined) {
    // The module may have loaded long before the first countdown mounted, so
    // refresh before handing out a snapshot. React re-reads it after
    // subscribing and picks the change up.
    now = Date.now();
    timer = setInterval(() => {
      now = Date.now();
      for (const listener of listeners) listener();
    }, TICK_MS);
  }

  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0) {
      clearInterval(timer);
      timer = undefined;
    }
  };
}

/** Current time, re-rendering every second while mounted. */
export function useNow(): number {
  return useSyncExternalStore(subscribe, () => now);
}
